/**
 * MavenPromo Sprint 2 - Product Fixes & Feature Enhancements
 * Jan-Feb 2026 Sprint with comprehensive bug fixes and features
 */

import type { Task, Sprint, Priority, TaskStatus } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-mavenpromo';

// Helper function to map user names to IDs
const getUserId = (name: string): string => {
  const userMap: { [key: string]: string } = {
    'Jakaria': 'user-jakaria',
    'Adil': 'user-adil',
    'Saif': 'user-saif',
    'Tias': 'user-tias',
    'Emtiaj': 'user-emtiaj',
    'Zarif': 'hossainzarif2002@gmail.com',
    'Rafi': 'user-rafi',
    'Masud': 'aidimentry',
    'Ekram': 'user-ekram',
    'Mahi vai': 'user-mahi',
    'Mahi': 'user-mahi',
    'Asif Vai': 'asif.redmond@gmail.com',
    'Asif': 'asif.redmond@gmail.com',
    'Khaled vai': 'user-khaled',
    'Team': 'user-team',
  };
  return userMap[name] || '';
};

// Second Sprint Tasks - Jan-Feb 2026 Product Fixes Sprint
export const mavenPromoProductFixesSprintTasks: Task[] = [
  {
    id: 'MP-FEB7-LANYARD',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lanyard - Wrist lanyard doesn\'t show customization',
    description: 'Wrist lanyard product not displaying customization preview correctly. URL: https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [getUserId('Jakaria')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customization',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'React, Customization Studio',
    phase: 'Sprint',
    deliverables: 'Fixed lanyard customization display',
    notes: 'Reported by Masud on Feb 7 2026, Verified by Adil',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-07T00:00:00Z',
    updatedAt: '2026-02-07T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Product', url: 'https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9' }],
    changeLogs: [],
    reportedBy: getUserId('Masud'),
    verifiedBy: getUserId('Adil'),
  },
  {
    id: 'MP-FEB5-CAP',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Black cap disaster - mockup and mask issues',
    description: 'Black cap showing multiple issues: Only one mockup when thumbnails show more, black mask appearing with mockup, product creation steps not followed properly causing issues',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: [getUserId('Saif')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customization',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 22,
    dependencies: '',
    skillsRequired: 'Design Studio, Product Configuration',
    phase: 'Sprint',
    deliverables: 'Fixed cap mockup generation, created FAQ for proper product setup',
    notes: 'Reported by Asif Vai on Feb 6 2026, Verified by Tias and Asif. Need to create FAQ explaining proper product setup',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-06T00:00:00Z',
    updatedAt: '2026-02-06T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Asif'),
    verifiedBy: getUserId('Tias'),
  },
  {
    id: 'MP-FEB8-CATEGORIES',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Category page bugs - popup and images',
    description: 'Multiple issues: Catalog popup not appearing on categories page (was disabled), some categories missing images, some subcategories showing with no products, category images stuck in infinite loop waiting for URL',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: [getUserId('Jakaria')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-catalog',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'React, Category Management',
    phase: 'Sprint',
    deliverables: 'Fixed catalog popup, category images, and empty subcategories',
    notes: 'Reported by Masud on Feb 8 2026, Verified by Masud',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-08T00:00:00Z',
    updatedAt: '2026-02-08T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Masud'),
    verifiedBy: getUserId('Masud'),
  },
  {
    id: 'MP-FEB21-COLORBUG',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Incorrect color shown in the cart',
    description: 'Cart displaying wrong color for products',
    type: 'Bug',
    status: 'Completed',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [getUserId('Adil')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Cart Logic, React',
    phase: 'Sprint',
    deliverables: 'Fixed cart color display',
    notes: 'Reported by Masud on Feb 21 2026, Verified by Masud',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-21T00:00:00Z',
    updatedAt: '2026-02-21T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Masud'),
    verifiedBy: getUserId('Masud'),
  },
  {
    id: 'MP-JAN8-BUGS-VERIFICATION',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Review past bugs for completeness',
    description: 'Comprehensive review of all past bugs to ensure proper resolution and verification',
    type: 'Verification',
    status: 'In Progress',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'QA',
    assigneeIds: [getUserId('Tias')],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 22,
    dependencies: '',
    skillsRequired: 'QA, Testing',
    phase: 'Sprint',
    deliverables: 'Verified bug resolution list',
    notes: 'Team task assigned on Feb 6 2026',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-06T00:00:00Z',
    updatedAt: '2026-02-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Team'),
  },
  {
    id: 'MP-LANYARD-PRODUCT',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Finalize lanyard production order with measurement',
    description: 'Review lanyard production specifications and measurements with Emtiaj',
    type: 'Bug',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Product',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 1,
    estimatedHours: 8,
    llmAdjustedHours: 8,
    dependencies: '',
    skillsRequired: 'Product Management',
    phase: 'Sprint',
    deliverables: 'Finalized lanyard production order',
    notes: 'Reported by Jakaria on Feb 4 2026, Meeting between Jakaria and Emtiaj, Verified by Jakaria',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-04T00:00:00Z',
    updatedAt: '2026-02-04T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Jakaria'),
  },
  {
    id: 'MP-CRASH1-CLIPART',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Provide raw vector files for production order',
    description: 'Need to provide raw vector file / high definition png of the logo/images/cliparts used in customization along with the production order',
    type: 'Feature',
    status: 'Completed',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Production',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'File Management, Admin Panel',
    phase: 'Sprint',
    deliverables: 'Vector file export for production orders',
    notes: 'Reported by Mahi vai on Feb 1 2026, Verified by Tias. Need more info. Not clear. Action Item: Emtiaj to reach out Mahi. Related to production order.',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-CLIPART-MERGE',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Merging Lanyard and regular clipart',
    description: 'Merge lanyard-specific clipart with regular clipart library',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customization',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Clipart Management',
    phase: 'Sprint',
    deliverables: 'Unified clipart library',
    notes: 'Reported by Mahi vai on Jan 25 2026, Verified by Tias. Requires clarification (Task description taken from emtiaz\'s message)',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-25T00:00:00Z',
    updatedAt: '2026-01-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-REGULAR-SHAPES',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Option to design with regular shapes in design studio',
    description: 'Add support for designing with regular shapes (circles, squares, triangles, etc.) in the design studio',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [getUserId('Zarif')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customization',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'Design Studio, Canvas API',
    phase: 'Sprint',
    deliverables: 'Regular shapes tool in design studio',
    notes: 'Reported by Mahi vai on Jan 25 2026, Verified by Tias',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-25T00:00:00Z',
    updatedAt: '2026-01-25T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-HOME-POPUP',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Home page popup modal configuration',
    description: 'When a user visits home page, show a pop-up modal if configured from the admin site',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: [getUserId('Zarif')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'React, Modal UI',
    phase: 'Sprint',
    deliverables: 'Home page popup modal system',
    notes: 'Reported by Mahi vai on Jan 25 2026, Verified by Tias. Now Not Work - Need every refresh POP up this ad',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-25T00:00:00Z',
    updatedAt: '2026-01-25T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-R001-PRICETABLE',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Moveable columns in price chart configuration',
    description: 'In price chart configuration, need ability to move columns. For example, have 5 columns 1, 50, 100, 500 and want to add 25 and move the column',
    type: 'Feature',
    status: 'Completed',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [getUserId('Adil')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-admin',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Admin Panel, Price Configuration',
    phase: 'Sprint',
    deliverables: 'Moveable price chart columns',
    notes: 'Reported by Asif Vai on Jan 30 2026, Verified by Tias',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-30T00:00:00Z',
    updatedAt: '2026-01-30T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Asif'),
  },
  {
    id: 'MP-CATEGORY-POPUP-UI',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Show catalog popup when clicking on category',
    description: 'When clicking on category, let the popup show',
    type: 'Feature',
    status: 'Completed',
    priority: 'Low',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: [getUserId('Adil')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-catalog',
    estimatedDays: 1,
    estimatedHours: 8,
    llmAdjustedHours: 8,
    dependencies: '',
    skillsRequired: 'React, UI/UX',
    phase: 'Sprint',
    deliverables: 'Category click popup',
    notes: 'Reported by Asif Vai on Jan 30 2026, Verified by Tias. Ask the group with mockup snapshot tagging Mahi, Asif and Khaled',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-30T00:00:00Z',
    updatedAt: '2026-01-30T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Asif'),
  },
  {
    id: 'MP-PRODUCT-PAGE-BUG',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Product images, description not appearing in product page',
    description: 'Product page not displaying images and descriptions correctly',
    type: 'Bug',
    status: 'Completed',
    priority: 'High',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-catalog',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'React, Product Pages',
    phase: 'Sprint',
    deliverables: 'Fixed product page display',
    notes: 'Reported by Asif Vai on Jan 30 2026, Verified by Tias',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-30T00:00:00Z',
    updatedAt: '2026-01-30T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Asif'),
  },
  {
    id: 'MP-ADD-DESIGN-BUTTON',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Change Add Design button text to be more intuitive',
    description: 'Change the button text of Add Design button to something more intuitive (example: customize your product)',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-customization',
    estimatedDays: 1,
    estimatedHours: 4,
    llmAdjustedHours: 4,
    dependencies: '',
    skillsRequired: 'UI/UX',
    phase: 'Sprint',
    deliverables: 'Updated button text',
    notes: 'Reported by Mahi vai on Feb 1 2026, Verified by Tias. Ask the group with mockup snapshot tagging Mahi, Asif and Khaled',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-WRISTBAND-SUBCATEGORY',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Wristband category not showing subcategories',
    description: 'In category page, clicking on Wristband category not showing its subcategories on the right as cards, rather showing empty',
    type: 'Bug',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Frontend',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-catalog',
    estimatedDays: 1,
    estimatedHours: 8,
    llmAdjustedHours: 8,
    dependencies: '',
    skillsRequired: 'React, Category Management',
    phase: 'Sprint',
    deliverables: 'Fixed subcategory display',
    notes: 'Reported by Mahi vai on Feb 1 2026, Verified by Tias. Not a bug. Previous design decision was to show only products if the parent category had both products and subcategories.',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-THUMBNAIL-GUIDELINES',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Provide guidelines on thumbnail image ratio',
    description: 'Need to provide guidelines on thumbnail image ratio so that images appear as expected',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Documentation',
    assigneeIds: [getUserId('Saif')],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 1,
    estimatedHours: 4,
    llmAdjustedHours: 4,
    dependencies: '',
    skillsRequired: 'Documentation',
    phase: 'Sprint',
    deliverables: 'Thumbnail image specifications documentation',
    notes: 'Reported by Mahi vai on Feb 1 2026, Verified by Tias. Multiple sizes shown in site thumbnail/Product. Documentation - provide thumbnails and image specifications to the admins so that they can upload images with exact configuration',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-NEGATIVE-PRICE',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Bug: negative price is not adding to the whole price',
    description: 'Negative price adjustments not being calculated correctly in total price',
    type: 'Bug',
    status: 'Open',
    priority: 'High',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [getUserId('Rafi')],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-mp-checkout',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'Price Calculation',
    phase: 'Sprint',
    deliverables: 'Fixed negative price calculation',
    notes: 'Reported by Mahi vai on Feb 22, Verified by Ekram',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-22T00:00:00Z',
    updatedAt: '2026-02-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    reportedBy: getUserId('Mahi'),
  },
  {
    id: 'MP-APPLICATION-ERROR-404',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Application error: client-side exception on lanyard page',
    description: 'Application error: a client-side exception has occurred while loading mavenpromo.com. Failed to load resource: the server responded with a status of 404 (). Link: https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9',
    type: 'Bug',
    status: 'Open',
    priority: 'Critical',
    complexity: 'Medium',
    category: 'Frontend',
    assigneeIds: [getUserId('Emtiaj')],
    parentTaskIds: [],
    subtaskIds: [],
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 14,
    dependencies: '',
    skillsRequired: 'React, Debugging',
    phase: 'Sprint',
    deliverables: 'Fixed 404 error on product page',
    notes: 'Reported by Masud on Feb 22, Verified by Ekram',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-22T00:00:00Z',
    updatedAt: '2026-02-22T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Product', url: 'https://mavenpromo.com/products/3wkeblCwchTiuibYeSW9' }],
    changeLogs: [],
    reportedBy: getUserId('Masud'),
  },
];

// Create second sprint
export const mavenPromoProductFixesSprint: Sprint = {
  id: 'sprint-mavenpromo-product-fixes-jan-feb-2026',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'MavenPromo - Product Fixes & Feature Enhancements (Jan-Feb 2026)',
  startDate: '2026-01-25',
  endDate: '2026-02-28',
  status: 'Active',
  taskIds: mavenPromoProductFixesSprintTasks.map(t => t.id),
  goals: 'Comprehensive product fixes, UI/UX improvements, and feature enhancements across the platform including category management, color selection, clipart handling, admin panel improvements, and mobile optimizations.',
  createdAt: '2026-01-25T00:00:00Z',
  updatedAt: '2026-02-22T00:00:00Z',
};
